<template>
  <div>
    <Breadcrumbs :items="items" />
    <v-sheet class="transparent-background-color pa-4">
      <div>
        <v-expansion-panels v-model="panels" multiple accordion>
          <v-expansion-panel>
            <v-expansion-panel-header>
              <v-card-title class="ms-n4 py-0">
                {{ $t("CRUD.info", [$t("Wave.ObjectName")]) }}
                <v-chip
                  small
                  label
                  class="ms-4 mt-1"
                  :color="wave.status | waveStatusColor"
                  :text-color="wave.status | waveStatusTextColor"
                  >{{ wave.status | waveStatusText }}
                </v-chip>
                <v-spacer />
                <v-sheet
                  class="pt-1 transparent-background-color d-inline-block"
                >
                  <v-btn
                    v-if="wave.actions.force_complete_sorting"
                    small
                    dark
                    color="danger"
                    class="px-2 me-2 mb-1"
                    @click.stop=""
                  >
                    <v-icon x-small class="pe-1">fa-check-double</v-icon>
                    {{ $t("Wave.actions.forceCompleteSorting") }}
                  </v-btn>
                  <v-btn
                    small
                    dark
                    color="print"
                    class="px-2 me-2 mb-1"
                    @click.stop=""
                  >
                    <v-icon x-small class="pe-1">fa-print</v-icon>
                    {{ $t("Wave.DetailFields.wave_detail_print") }}
                  </v-btn>
                </v-sheet>
              </v-card-title>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-divider />
              <v-row no-gutters class="mt-4 table-style">
                <v-col cols="12" md="3">
                  <v-card class="pa-2 text-no-wrap" outlined tile>
                    <span class="secondary--text">
                      {{ $t("Wave.Fields.wave_code") }}{{ $t("Global.colon") }}
                    </span>
                    {{ wave.wave_code }}
                  </v-card>
                </v-col>
                <v-col cols="12" md="3">
                  <v-card class="pa-2 text-no-wrap d-flex" outlined tile>
                    <span class="secondary--text">
                      {{ $t("Wave.Fields.status") }}{{ $t("Global.colon") }}
                    </span>
                    <span>
                      {{ wave.status | waveStatusText }}
                    </span>
                  </v-card>
                </v-col>
                <v-col cols="12" md="3">
                  <v-card class="pa-2 text-no-wrap" outlined tile>
                    <span class="secondary--text">
                      {{ $t("Wave.Fields.created_time")
                      }}{{ $t("Global.colon") }}
                    </span>
                    {{ wave.created | formatTime }}
                  </v-card>
                </v-col>
                <v-col cols="12" md="3">
                  <v-card class="pa-2 text-no-wrap" outlined tile>
                    <span class="secondary--text">
                      {{ $t("Wave.Fields.stock_order_num")
                      }}{{ $t("Global.colon") }}
                    </span>
                    {{ wave.stock_order_num }}
                  </v-card>
                </v-col>

                <v-col cols="12" md="3">
                  <v-card class="pa-2 text-no-wrap d-flex" outlined tile>
                    <span class="secondary--text">
                      {{ $t("Wave.Fields.receive_user")
                      }}{{ $t("Global.colon") }}
                    </span>
                    {{ wave.name }}
                  </v-card>
                </v-col>
                <v-col cols="12" md="3">
                  <v-card class="pa-2 text-no-wrap" outlined tile>
                    <span class="secondary--text">
                      {{ $t("Wave.Fields.receive_time")
                      }}{{ $t("Global.colon") }}
                    </span>
                    {{ wave.receive_time | formatTime }}
                  </v-card>
                </v-col>
                <v-col cols="12" md="3">
                  <v-card class="pa-2 text-no-wrap d-flex" outlined tile>
                    <span class="secondary--text">
                      {{ $t("Wave.DetailFields.stock_name")
                      }}{{ $t("Global.colon") }}
                    </span>
                    {{ wave.stock_name }}
                  </v-card>
                </v-col>
                <v-col cols="12" md="3">
                  <v-card class="pa-2 text-no-wrap d-flex" outlined tile>
                    <span class="secondary--text">
                      {{ $t("Wave.DetailFields.stock_order_out_type")
                      }}{{ $t("Global.colon") }}
                    </span>
                    {{ wave.type | stockOrderOUTTypesText }}
                  </v-card>
                </v-col>
              </v-row>
            </v-expansion-panel-content>
          </v-expansion-panel>
          <v-expansion-panel>
            <v-expansion-panel-header class="py-0">
              <v-tabs v-model="tab" background-color="transparent">
                <v-tab
                  @click.stop="
                    tab = 0;
                    panels.find((p) => p == 1) ? null : panels.push(1);
                  "
                >
                  {{ $t("Order.actions.activities") }}
                </v-tab>
              </v-tabs>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-tabs-items v-model="tab">
                <v-tab-item>
                  <v-card flat>
                    <v-data-table
                      :headers="activitiesHeaders"
                      :items="stock_order_wave_activities"
                      class="elevation-0"
                      disable-pagination
                      hide-default-footer
                    >
                      <template v-slot:item.created="{ item }">
                        <span class="text-no-wrap">
                          {{ item.created | formatTime }}
                        </span>
                      </template>
                    </v-data-table>
                  </v-card>
                </v-tab-item>
              </v-tabs-items>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>

        <v-data-table
          id="wave_details_table"
          :headers="detailsHeaders"
          :items="stock_order_wave_details"
          :search="filter"
          multi-sort
          class="elevation-2 px-4 mt-4"
          disable-pagination
          hide-default-footer
        >
          <template v-slot:top>
            <v-toolbar flat class="transparent-background-color">
              <v-toolbar-title class="ms-n2">
                {{ $t("CRUD.details", [$t("Wave.ObjectName")]) }}
              </v-toolbar-title>
            </v-toolbar>
            <v-divider />
            <v-toolbar
              flat
              class="transparent-background-color"
              align-self="center"
            >
              <v-row no-gutters class="me-n3">
                <span class="caption secondary--text pt-2">
                  {{
                    $t(
                      "$vuetify.dataFooter.pageText",
                      wave.stock_order_wave_details
                        ? [
                            1,
                            wave.stock_order_wave_details.length,
                            wave.stock_order_wave_details.length,
                          ]
                        : [0, 0, 0]
                    )
                  }}
                </span>
                <v-row no-gutters>
                  <v-spacer />
                  <v-col cols="12" sm="9" md="8">
                    <v-text-field
                      v-model="filter"
                      clearable
                      hide-details
                      :label="$t('Search.filter')"
                    />
                  </v-col>
                </v-row>
              </v-row>
            </v-toolbar>
          </template>
          <template v-slot:item.type="{ item }">
            <v-chip
              small
              label
              :color="item.type | stockOrderOUTTypesColor"
              :text-color="item.type | stockOrderOUTTypesTextColor"
              >{{ item.type | stockOrderOUTTypesText }}
            </v-chip>
          </template>
          <template v-slot:item.stock_order_code="{ item }">
            <a @click="$router.push('/stock-orders-out/' + item.stock_order_id)">
              {{ item.stock_order_code }}
            </a>
          </template>
        </v-data-table>
      </div>
    </v-sheet>
  </div>
</template>

<script>
import { mapState } from "vuex";
import i18n from "@/i18n";
import Breadcrumbs from "@/components/Breadcrumbs";
import Waves from "@/api/waves";

export default {
  components: {
    Breadcrumbs,
  },
  computed: {
    items() {
      var items = [
        {
          text: i18n.t("Nav.home"),
          disabled: false,
          replace: true,
          href: "/home",
        },
        {
          text: i18n.t("Nav.stockManagement"),
          disabled: false,
          replace: true,
          href: "/stocks",
        },
        {
          text: i18n.t("Nav.waves"),
          disabled: false,
          href: "/waves",
        },
        {
          text: i18n.t("Nav.waveDetail"),
          disabled: true,
        },
        {
          text: this.wave && this.wave.wave_code ? this.wave.wave_code : "",
          disabled: false,
          accent: true,
        },
      ];
      return items;
    },
    detailsHeaders() {
      var headers = [
        {
          text: i18n.t("Wave.DetailFields.wave_obj_name"),
          align: "center",
          value: "obj_name",
        },
        {
          text: i18n.t("Wave.DetailFields.stock_order_out_code"),
          align: "start",
          value: "stock_order_code",
        },
        {
          text: i18n.t("Wave.DetailFields.stock_order_out_type"),
          align: "center",
          value: "type",
        },
        {
          text: i18n.t("Wave.DetailFields.stock_order_out_created_time"),
          align: "center",
          value: "created",
        },
      ];

      return headers;
    },
    activitiesHeaders: () => [
      {
        text: i18n.t("Wave.ActivityFields.created_time"),
        align: "start",
        sortable: false,
        value: "created",
      },
      {
        text: i18n.t("Wave.ActivityFields.content"),
        align: "start",
        sortable: false,
        value: "content",
      },
      {
        text: i18n.t("Wave.ActivityFields.operater"),
        align: "start",
        sortable: false,
        value: "operater_name",
      },
    ],
    // 使用对象展开运算符将此对象混入到外部对象中
    ...mapState(["loading"]),
  },

  mounted() {
    this.mode = this.$route.params.mode;
    if (this.mode) this.$router.replace("/waves/" + this.$route.params.id);
  },

  data: () => ({
    panels: [0],
    tab: null,
    filter: "",
    mode: "",
    wave: {
      actions: {},
    },
    stock_order_wave_details: [],
    stock_order_wave_activities: [],
  }),

  methods: {
    getWaveDetailFromAPI() {
      Waves.fetchOne(this.$route.params.id, { order_activity: 1 }).then(
        function (response) {
          if (response.data.status.isOK()) {
            this.wave = response.data.wave;
            this.stock_order_wave_details =
              response.data.wave.stock_order_wave_details;
            this.stock_order_wave_activities = response.data.order_activities;
          }
        }.bind(this)
      );
    },
  },

  watch: {
    $route(to) {
      if (to.params.mode) {
        this.mode = to.params.mode;
        this.$router.replace("/waves/" + to.params.id);
      }
    },
    mode: {
      handler() {
        if (this.$route.params.id) {
          this.getWaveDetailFromAPI();
        }
      },
    },
  },
};
</script>